import React, { useState, useRef } from "react";
import { Popover, Spinner } from "@shopify/polaris";
import { Clock, Check, Eye } from "lucide-react";
import { ChatMessage, ChatReader, Conversation } from "types/Chat";
import { fetchReaders } from "services/chat.service";
import UserAvatar from "components/user-avatar/user-avatar";
import { hoverHandlers } from "./chat-touch";

interface StatusProps {
  message: ChatMessage;
  conversation: Conversation;
  companyId: string;
}

// Padding that widens the 12px status icon into a finger-sized target on touch screens
// without moving its neighbours (the negative margin takes the padding back).
const TOUCH_TARGET = "[@media(hover:none)]:-m-2 [@media(hover:none)]:p-2";

/** The status icon as a button: a mouse hovers it, a finger or the keyboard presses it. */
const ActivatorButton: React.FC<{
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, onClick, children }) => (
  <button type="button" aria-label={label} onClick={onClick} className={TOUCH_TARGET}>
    {children}
  </button>
);

/** Small helper that wires hover (or tap) → Popover for the static (non-list) status badges. */
const StatusTooltip: React.FC<{
  active: boolean;
  onChange: (next: boolean) => void;
  label: string;
  activator: React.ReactNode;
  children: React.ReactNode;
}> = ({ active, onChange, label, activator, children }) => {
  const leaveTimeoutRef = useRef<number | null>(null);

  const handleEnter = () => {
    if (leaveTimeoutRef.current) window.clearTimeout(leaveTimeoutRef.current);
    onChange(true);
  };

  const handleLeave = () => {
    leaveTimeoutRef.current = window.setTimeout(() => {
      onChange(false);
    }, 200);
  };

  return (
    <div {...hoverHandlers(handleEnter, handleLeave)}>
      <Popover
        active={active}
        activator={
          <ActivatorButton label={label} onClick={handleEnter}>
            {activator}
          </ActivatorButton>
        }
        onClose={() => onChange(false)}
        preferredPosition="below"
        fluidContent={true}
      >
        <div {...hoverHandlers(handleEnter, handleLeave)}>{children}</div>
      </Popover>
    </div>
  );
};

export const MessageStatus: React.FC<StatusProps> = ({ message, conversation, companyId }) => {
  const [popoverActive, setPopoverActive] = useState(false);
  const [readers, setReaders] = useState<ChatReader[] | null>(null);
  const [readersLoading, setReadersLoading] = useState(false);
  const leaveTimeoutRef = useRef<number | null>(null);
  const cachedAtMaxRef = useRef<number | null>(null);

  if (message.uploadStatus === "pending" || message.uploadStatus === "uploading") {
    return (
      <StatusTooltip
        active={popoverActive}
        onChange={setPopoverActive}
        label="Pending"
        activator={<Clock size={12} className="text-gray-500 cursor-help" />}
      >
        <div className="px-2 py-1 bg-gray-900 border border-gray-700 shadow-xl rounded text-[10px] text-gray-300 font-medium uppercase tracking-wider">
          Pending
        </div>
      </StatusTooltip>
    );
  }

  const maxOther = conversation.maxOtherLastReadAt || 0;
  const isRead = maxOther >= message.createdAt;

  if (!isRead) {
    return (
      <StatusTooltip
        active={popoverActive}
        onChange={setPopoverActive}
        label="Sent"
        activator={<Check size={12} className="text-gray-400 cursor-help" />}
      >
        <div className="px-2 py-1 bg-gray-900 border border-gray-700 shadow-xl rounded text-[10px] text-gray-300 font-medium uppercase tracking-wider">
          Sent
        </div>
      </StatusTooltip>
    );
  }

  const eyeIcon = <Eye size={12} className="text-blue-400" />;

  if (conversation.type === "dm") {
    return (
      <StatusTooltip
        active={popoverActive}
        onChange={setPopoverActive}
        label="Seen"
        activator={eyeIcon}
      >
        <div className="px-2 py-1 bg-gray-900 border border-gray-700 shadow-xl rounded text-[10px] text-blue-400 font-bold uppercase tracking-wider">
          {"Seen"}
        </div>
      </StatusTooltip>
    );
  }

  const handleEnter = async () => {
    if (leaveTimeoutRef.current) window.clearTimeout(leaveTimeoutRef.current);
    setPopoverActive(true);
    if (
      readers !== null &&
      cachedAtMaxRef.current !== null &&
      maxOther <= cachedAtMaxRef.current
    ) {
      return;
    }
    if (readersLoading) return;
    setReadersLoading(true);
    const snapshotMax = maxOther;
    try {
      const list = await fetchReaders(companyId, conversation.id, message.createdAt);
      setReaders(list);
      cachedAtMaxRef.current = snapshotMax;
    } catch {
      setReaders([]);
    } finally {
      setReadersLoading(false);
    }
  };

  const handleLeave = () => {
    leaveTimeoutRef.current = window.setTimeout(() => {
      setPopoverActive(false);
    }, 200);
  };

  return (
    <div {...hoverHandlers(handleEnter, handleLeave)}>
      <Popover
        active={popoverActive}
        activator={
          <ActivatorButton label="Viewed by" onClick={handleEnter}>
            {eyeIcon}
          </ActivatorButton>
        }
        onClose={() => setPopoverActive(false)}
        preferredPosition="below"
        fluidContent={true}
      >
        <div
          className="p-3 min-w-[200px] max-w-[280px] bg-gray-900 border border-gray-700 shadow-xl rounded-lg"
          {...hoverHandlers(handleEnter, handleLeave)}
        >
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3 border-b border-gray-800 pb-1.5 font-bold">
            Viewed by
          </div>
          {readersLoading && readers === null ? (
            <div className="flex justify-center py-2"><Spinner size="small" /></div>
          ) : !readers || readers.length === 0 ? (
            <div className="text-xs text-gray-500">—</div>
          ) : (
            <ul className="flex flex-col gap-2.5">
              {readers.map((r) => (
                <li key={r.userId} className="flex items-center gap-2.5 group/item">
                  <UserAvatar
                    companyId={companyId}
                    user={{ id: r.userId, name: r.name, avatarFileId: r.avatarFileId }}
                    size={28}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-xs font-semibold text-gray-100 truncate group-hover/item:text-blue-300 transition-colors">
                      {r.name}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Popover>
    </div>
  );
};
